/**
 * Session lifetime rules (kcv2 parity):
 *
 *   1. Browser closed → signed out. `kc3.alive` is a JS session cookie (no
 *      Expires) set while a user is signed in; the browser drops it on exit.
 *      main.tsx treats "persisted user but no kc3.alive" as a new browser
 *      session and signs out before render. The kc3.* auth cookies are session
 *      cookies too (mint-session-cookies.ts), so the server side dies with it.
 *   2. Tab closed (or refreshed) → signed out, unless another signed-in tab of
 *      the app is still open (admin pages allow several tabs; the agent portal
 *      allows one — SingleTabGuard). Done on `pagehide` with a keepalive
 *      logout, plus clearing the persisted session synchronously.
 *   3. Another tab signs in as someone else / signs out → this tab follows
 *      (cookies are shared per browser, so it would otherwise act as the new
 *      user while still showing the old one).
 *
 * Only tabs that rendered the signed-in shell (AppShell) register as live
 * tabs — login pages, the blocked "second agent tab" screen, popups (email
 * OAuth) and the client-portal preview never trigger or prevent a logout.
 */
import { useAuthStore } from '../store/authStore.js'
import { getExistingSocket } from '../socket/client.js'
import { RELOAD_LOGOUT_KEY } from './reloadLogout.js'
import { loginPathFor } from './loginPath.js'

const ALIVE_COOKIE = 'kc3.alive'
const TABS_CHANNEL = 'kc3-app-tabs'
const HEARTBEAT_MS = 2_000
const PEER_TTL_MS = 5_000

export function hasAliveCookie(): boolean {
  return document.cookie.split(';').some((c) => c.trim() === `${ALIVE_COOKIE}=1`)
}

export function setAliveCookie(on: boolean): void {
  document.cookie = on
    ? `${ALIVE_COOKIE}=1; path=/; SameSite=Lax`
    : `${ALIVE_COOKIE}=; path=/; SameSite=Lax; Max-Age=0`
}

/** Agent portal routes (`/` redirects to /agent). */
export function isAgentArea(pathname: string): boolean {
  return pathname === '/' || /^\/(agent|supervisor)(\/|$)/.test(pathname)
}

/**
 * Revoke the server session. `keepalive` lets the request outlive the page
 * (tab close / reload); unlike sendBeacon it can carry the CSRF header.
 */
export function sendLogout(csrfToken: string | null, keepalive: boolean): Promise<unknown> {
  return fetch('/api/auth/logout', {
    method: 'POST',
    credentials: 'include',
    keepalive,
    headers: { 'content-type': 'application/json', ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}) },
    body: '{}',
  }).catch(() => undefined)
}

/** Sign out locally + on the server, and make the next load in this tab land on login. */
export function endSessionNow(keepalive: boolean): Promise<unknown> {
  const { user, csrfToken, clearSession } = useAuthStore.getState()
  if (!user) return Promise.resolve()
  try {
    sessionStorage.setItem(RELOAD_LOGOUT_KEY, '1')
  } catch { /* private mode */ }
  try {
    const socket = getExistingSocket()
    if (socket?.connected) socket.emit('agent:status', { event: 'logout' })
  } catch { /* best effort */ }
  const req = sendLogout(csrfToken, keepalive)
  setAliveCookie(false)
  clearSession()
  return req
}

function isEmbedded(): boolean {
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

/** Keep kc3.alive in step with the signed-in state (call once at startup). */
export function syncAliveCookie(): () => void {
  if (useAuthStore.getState().user) setAliveCookie(true)
  return useAuthStore.subscribe((s, prev) => {
    if (Boolean(s.user) !== Boolean(prev.user)) setAliveCookie(Boolean(s.user))
  })
}

/**
 * Rules 2 + 3. Call from the signed-in shell; returns a cleanup.
 */
export function installTabSessionLifecycle(): () => void {
  if (typeof window === 'undefined' || window.opener || isEmbedded()) return () => undefined

  const tabId = crypto.randomUUID()
  const peers = new Map<string, number>()
  const channel = 'BroadcastChannel' in window ? new BroadcastChannel(TABS_CHANNEL) : null
  const post = (t: 'hello' | 'here' | 'bye'): void => channel?.postMessage({ t, id: tabId })
  if (channel) {
    channel.onmessage = (ev: MessageEvent<{ t: string; id: string }>) => {
      const { t, id } = ev.data ?? {}
      if (!id || id === tabId) return
      if (t === 'bye') peers.delete(id)
      else {
        peers.set(id, Date.now())
        if (t === 'hello') post('here')
      }
    }
  }
  post('hello')
  const beat = setInterval(() => post('here'), HEARTBEAT_MS)
  const othersAlive = (): boolean => [...peers.values()].some((ts) => Date.now() - ts < PEER_TTL_MS)

  const onPageHide = (): void => {
    post('bye')
    if (othersAlive()) return
    void endSessionNow(true)
  }
  // A bfcache restore would revive a page whose session was just ended.
  const onPageShow = (ev: PageTransitionEvent): void => {
    if (ev.persisted && !useAuthStore.getState().user) window.location.replace(loginPathFor(window.location.pathname))
  }
  // Rule 3: the persisted session changed in another tab.
  const myUserId = useAuthStore.getState().user?.id ?? null
  const onStorage = (ev: StorageEvent): void => {
    if (ev.key !== 'kc3-auth-storage') return
    let nextId: string | null = null
    try {
      nextId = (JSON.parse(ev.newValue ?? 'null') as { state?: { user?: { id?: string } | null } } | null)?.state?.user?.id ?? null
    } catch { /* unreadable → treat as signed out */ }
    if (nextId === myUserId) return
    // Another tab signed in as someone else (or out): this tab's view is stale.
    window.location.replace(loginPathFor(window.location.pathname))
  }

  window.addEventListener('pagehide', onPageHide)
  window.addEventListener('pageshow', onPageShow)
  window.addEventListener('storage', onStorage)
  return () => {
    clearInterval(beat)
    window.removeEventListener('pagehide', onPageHide)
    window.removeEventListener('pageshow', onPageShow)
    window.removeEventListener('storage', onStorage)
    post('bye')
    channel?.close()
  }
}
